'use client'

import { useEffect, useRef, useState } from 'react'
import { useWindowSize } from '@/hooks/useWindowSize'
export interface ImageMeta {
  width: number
  height: number
  imageUrl: string
}

// Processes an image to fit container dimensions while maintaining aspect ratio
async function processImage(
  imageUrl: string,
  image: HTMLImageElement,
  containerWidth: number,
  containerHeight: number,
  signal: AbortSignal,
): Promise<ImageMeta | null> {
  return new Promise((resolve) => {
    image.onload = () => {
      // Check if operation was cancelled
      if (signal.aborted) {
        resolve(null)
        return
      }

      const imageAspect = image.naturalWidth / image.naturalHeight
      const containerAspect = containerWidth / containerHeight

      let width: number
      let height: number

      if (containerAspect > imageAspect) {
        width = containerWidth
        height = containerWidth / imageAspect
      } else {
        height = containerHeight
        width = containerHeight * imageAspect
      }

      const canvas = document.createElement('canvas')
      canvas.width = containerWidth
      canvas.height = containerHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }

      // Center the image
      const x = (containerWidth - width) / 2
      const y = (containerHeight - height) / 2
      ctx.drawImage(image, x, y, width, height)

      resolve({
        width: containerWidth,
        height: containerHeight,
        imageUrl: canvas.toDataURL('image/png'),
      })
    }

    image.src = imageUrl
  })
}

// Custom hook to manage container dimensions and image processing
export function useImage(containerRef: React.RefObject<HTMLDivElement | null>, imageUrl: string) {
  const [meta, setMeta] = useState<ImageMeta | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const updateDimensions = async () => {
    const container = containerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()

    // Skip if dimensions haven't changed
    if (meta && meta.width === rect.width && meta.height === rect.height) {
      return
    }

    // Cancel any pending image processing
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller for this operation
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    // Reset dimensions to trigger unmount
    setMeta(null)

    // Process the image with the new dimensions
    const img = new Image()
    const result = await processImage(
      imageUrl,
      img,
      rect.width,
      rect.height,
      abortController.signal,
    )

    if (result) {
      setMeta(result)
    }
  }

  // Handle window resize
  const { width, height } = useWindowSize()
  useEffect(() => {
    updateDimensions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height])

  // Initial update
  useEffect(() => {
    updateDimensions()
    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return meta
}
